import { type PropsWithChildren, useMemo } from 'react';
import logTrailCardClicked from '@alltrails/amplitude/events/logTrailCardClicked';
import { PageUIActionContext, type PageUIActions } from '@alltrails/layout/components/PageUI/PageUIActionContext/PageUIActionContext';
import useTrailNavigate, { useExploreTrailUrl } from '@/hooks/useTrailNavigate';

/**
 * Carries out the click actions in Ask's answer. Trails open through a client-side navigation.
 * Ask stays active through the navigation, so Back returns to the same answer from the cache.
 *
 * Switches on the page so that a page added to `PageUIRecordPage` fails to compile here until Ask can open it.
 */
const AskActionProvider = ({ children }: PropsWithChildren) => {
  const exploreTrailUrl = useExploreTrailUrl();
  const trailNavigate = useTrailNavigate();

  const value = useMemo<PageUIActions>(
    () => ({
      getHref: action => {
        switch (action.page) {
          case 'trail-detail':
            return exploreTrailUrl(action.slug);
        }
      },
      onClick: ({ clickAction, eventProperties }, event) => {
        switch (clickAction.page) {
          case 'trail-detail':
            logTrailCardClicked({
              card_location: 'ask alltrails',
              detailed_card_location: 'ask alltrails results',
              has_community_insights: eventProperties.hasInsight,
              trail_id: String(eventProperties.itemId)
            });
            trailNavigate(clickAction.slug, event);
            break;
        }
      }
    }),
    [exploreTrailUrl, trailNavigate]
  );

  return <PageUIActionContext.Provider value={value}>{children}</PageUIActionContext.Provider>;
};

export default AskActionProvider;
